import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { 
  MapPinIcon,
  StarIcon,
  ClockIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowLeftIcon,
  XMarkIcon,
  EyeIcon,
  HeartIcon,
  FireIcon,
  SparklesIcon,
  TagIcon,
  TruckIcon,
  CurrencyRupeeIcon,
  ChevronRightIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { supabase } from '../config/supabase'
import toast from 'react-hot-toast'
import CustomerLoader from '../components/customer/CustomerLoader'
import logo from '../assets/logo green.png'

// Brand colors - Professional Food Delivery App Style
const BRAND_GREEN = '#00E676' // Header background
const ACTION_GREEN = '#00C853' // Buttons, active states
const BRAND_LIME = '#C6FF3D'
const BRAND_BLACK = '#2D2D2D'
const LIGHT_BG = '#F8F9FA'

const RestaurantDiscovery = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [restaurants, setRestaurants] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('location') || '')
  const [selectedCuisine, setSelectedCuisine] = useState('all')
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [favorites, setFavorites] = useState(new Set())
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)

  // Category cards for home page style
  const categoryCards = [
    { 
      id: 'drink_dine', 
      name: 'Drink & dine', 
      image: '🍕',
      description: 'Pizza, burgers & more'
    },
    { 
      id: 'buffet', 
      name: 'Buffet', 
      image: '🍱',
      description: 'All you can eat'
    },
    { 
      id: 'premium', 
      name: 'Premium dining', 
      image: '🍽️',
      description: 'Fine dining experience'
    },
    { 
      id: 'coffee', 
      name: 'Coffee Corner', 
      image: '☕',
      description: 'Cafes & desserts'
    }
  ]

  const cuisineTypes = [
    { id: 'all', name: 'All Cuisines', icon: '🍽️' },
    { id: 'indian', name: 'Indian', icon: '🍛' },
    { id: 'chinese', name: 'Chinese', icon: '🥡' },
    { id: 'italian', name: 'Italian', icon: '🍝' },
    { id: 'mexican', name: 'Mexican', icon: '🌮' },
    { id: 'thai', name: 'Thai', icon: '🍜' },
    { id: 'american', name: 'American', icon: '🍔' },
    { id: 'japanese', name: 'Japanese', icon: '🍱' }
  ]

  const filterOptions = [
    { id: 'all', name: 'All', icon: SparklesIcon },
    { id: 'open', name: 'Open Now', icon: ClockIcon },
    { id: 'top_rated', name: 'Top Rated', icon: StarIcon },
    { id: 'fast_delivery', name: 'Fast Delivery', icon: TruckIcon },
    { id: 'offers', name: 'Offers', icon: TagIcon }
  ]

  useEffect(() => {
    fetchRestaurants()
  }, [])

  const fetchRestaurants = async () => {
    try {
      setLoading(true)
      
      // Fetch restaurants from users table (simplified schema)
      const { data: restaurantsData, error: restaurantsError } = await supabase
        .from('users')
        .select(`
          id,
          full_name,
          email,
          restaurant_name,
          restaurant_description,
          restaurant_address,
          restaurant_phone,
          restaurant_email,
          cuisine_type,
          logo_url,
          banner_url,
          opening_hours,
          is_active,
          is_open,
          created_at,
          updated_at
        `)
        .eq('role', 'restaurant_owner')
        .eq('is_active', true)
        .not('restaurant_name', 'is', null)
        .order('created_at', { ascending: false })

      if (restaurantsError) throw restaurantsError

      // Fetch categories and menu items separately for each restaurant
      const restaurantsWithCounts = await Promise.all(
        (restaurantsData || []).map(async (restaurant) => {
          try {
            // Get categories count - categories.restaurant_id references users.id
            const { count: categoriesCount } = await supabase
              .from('categories')
              .select('*', { count: 'exact', head: true })
              .eq('restaurant_id', restaurant.id)
              .eq('is_active', true)

            // Get menu items count - menu_items.restaurant_id references users.id  
            const { count: menuItemsCount } = await supabase
              .from('menu_items')
              .select('*', { count: 'exact', head: true })
              .eq('restaurant_id', restaurant.id)
              .eq('is_available', true)

            // Get reviews for rating calculation
            const { data: reviews } = await supabase
              .from('reviews')
              .select('overall_rating')
              .eq('restaurant_id', restaurant.id)

            const avgRating = reviews?.length > 0 
              ? reviews.reduce((sum, review) => sum + review.overall_rating, 0) / reviews.length
              : 4.2

            return {
              ...restaurant,
              // Map users table fields to restaurant fields for compatibility
              name: restaurant.restaurant_name,
              description: restaurant.restaurant_description,
              address: restaurant.restaurant_address,
              phone: restaurant.restaurant_phone,
              email: restaurant.restaurant_email || restaurant.email,
              owner: {
                id: restaurant.id,
                full_name: restaurant.full_name,
                email: restaurant.email
              },
              rating: Number(avgRating.toFixed(1)),
              review_count: reviews?.length || 0,
              menu_count: menuItemsCount || 12,
              category_count: categoriesCount || 3
            }
          } catch (error) {
            console.warn(`Error fetching data for restaurant ${restaurant.id}:`, error)
            return {
              ...restaurant,
              rating: 4.2,
              review_count: 0,
              menu_count: 12,
              category_count: 3
            }
          }
        })
      )

      // Process restaurant data with fallback ratings
      const restaurantsWithRatings = restaurantsWithCounts

      setRestaurants(restaurantsWithRatings)
      
      // Set up real-time subscription for restaurant updates
      const subscription = supabase
        .channel('restaurants')
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'restaurants' },
          (payload) => {
            console.log('Restaurant updated:', payload)
            fetchRestaurants() // Refetch on changes
          }
        )
        .subscribe()

      return () => {
        subscription.unsubscribe()
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error)
      toast.error('Failed to load restaurants. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = (restaurant.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (restaurant.address || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (restaurant.cuisine_type || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (restaurant.description || '').toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesCuisine = selectedCuisine === 'all' || restaurant.cuisine_type === selectedCuisine
    
    let matchesFilter = true
    if (selectedFilter === 'open') {
      matchesFilter = isCurrentlyOpen(restaurant)
    } else if (selectedFilter === 'top_rated') {
      matchesFilter = (restaurant.rating || 0) >= 4.0
    } else if (selectedFilter === 'fast_delivery') {
      const deliveryTime = parseInt(restaurant.estimated_delivery_time || restaurant.delivery_time || '30')
      matchesFilter = deliveryTime <= 30
    } else if (selectedFilter === 'offers') {
      matchesFilter = restaurant.has_offers || Math.random() > 0.5 // Placeholder
    }
    
    return matchesSearch && matchesCuisine && matchesFilter
  })

  const handleRestaurantClick = (restaurant) => {
    // Navigate to restaurant details page for dine-out (booking)
    navigate(`/restaurant/${restaurant.id}`)
  }

  const handleViewMenu = (restaurant, e) => {
    e.stopPropagation()
    // Navigate to restaurant details page for dine-out (booking)
    navigate(`/restaurant/${restaurant.id}`)
  }

  const toggleFavorite = (restaurantId, e) => {
    e.stopPropagation()
    const newFavorites = new Set(favorites)
    if (newFavorites.has(restaurantId)) {
      newFavorites.delete(restaurantId)
      toast.success('Removed from favorites')
    } else {
      newFavorites.add(restaurantId)
      toast.success('Added to favorites! ❤️')
    }
    setFavorites(newFavorites)
  }

  const handleSearch = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    
    // If search query is not empty, navigate to search results page
    if (query.trim().length > 0) {
      // Navigate to dish-specific results page
      navigate(`/search?q=${encodeURIComponent(query)}`)
    }
  }

  const isCurrentlyOpen = (restaurant) => {
    // Default to open if opening_hours is missing (restaurant still setting up)
    if (!restaurant?.opening_hours) return true
    
    try {
      const hours = restaurant.opening_hours
      
      // Create IST time directly
      const now = new Date()
      // Add 5:30 hours to UTC to get IST
      const istOffset = 5.5 * 60 * 60 * 1000 // 5.5 hours in milliseconds
      const istTime = new Date(now.getTime() + istOffset)
      
      // Get current day in IST
      const today = istTime.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
      const todayHours = hours[today]
      
      // Get current time in HH:MM format
      const currentHour = istTime.getUTCHours().toString().padStart(2, '0')
      const currentMinute = istTime.getUTCMinutes().toString().padStart(2, '0')
      const currentTime = `${currentHour}:${currentMinute}`
      
      if (!todayHours || todayHours.closed === true) {
        return false
      }
      
      const openTime = todayHours.open
      const closeTime = todayHours.close
      
      // Convert time strings to minutes for comparison
      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number)
        return hours * 60 + minutes
      }
      
      const currentMinutes = timeToMinutes(currentTime)
      const openMinutes = timeToMinutes(openTime)
      const closeMinutes = timeToMinutes(closeTime)
      
      return currentMinutes >= openMinutes && currentMinutes <= closeMinutes
    } catch (error) {
      return true // Default to open if can't parse
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BRAND_LIME }}>
        {/* Playful Background - Matching Landing Page */}
        <div className="fixed inset-0 pointer-events-none">
          <motion.div 
            className="absolute top-20 right-10 w-32 h-32 rounded-full border-4 border-black/5"
            animate={{ y: [0, -20, 0], rotate: [0, 180, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-32 left-10 w-24 h-24 rounded-full bg-black/5"
            animate={{ y: [0, 20, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Dot Pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='15' cy='15' r='2' fill='%232D2D2D'/%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Loading Content */}
        <div className="relative z-10 text-center px-4">
          {/* Logo Container - Matching Landing Page Style */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring", bounce: 0.5 }}
            className="mb-8"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="bg-white rounded-2xl px-8 py-6 border-4 border-black shadow-[8px_8px_0_0_rgba(0,0,0,1)] inline-block"
            >
              <img src={logo} alt="Ordyrr" className="h-20 w-auto" />
            </motion.div>
          </motion.div>

          {/* Loading Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-2xl sm:text-3xl font-black text-black mb-3 tracking-tight">
              FINDING RESTAURANTS
            </h2>
            <p className="text-base font-bold text-black/70 mb-6">
              Discovering amazing food near you... 🍽️
            </p>
            
            {/* Animated Food Emojis */}
            <div className="flex items-center justify-center gap-4">
              {['🍕', '🍔', '🍜', '🍰'].map((emoji, i) => (
                <motion.div
                  key={i}
                  className="text-4xl"
                  animate={{
                    y: [0, -15, 0],
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.2, 1]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: "easeInOut"
                  }}
                >
                  {emoji}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Search Bar - Fixed */}
      <div className="bg-white sticky top-0 z-50 px-4 py-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div className="relative max-w-2xl mx-auto">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for  Localities, Dishes, Offers"
            className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm text-gray-700"
          />
          {searchQuery && (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-400 transition-colors"
            >
              <XMarkIcon className="w-4 h-4 text-white" />
            </motion.button>
          )}
        </div>
      </div>

      {/* Category Cards Section - Horizontal Scroll */}
      <div className="overflow-x-auto scrollbar-hide px-4 py-4">
        <div className="flex gap-3 pb-2">
          {categoryCards.map((category) => (
            <motion.div
              key={category.id}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                // Filter restaurants by category
                toast.success(`Showing ${category.name}`)
              }}
              className="flex-shrink-0 cursor-pointer"
            >
              <div className="flex flex-col items-center">
                <div className="text-xs font-semibold text-gray-900 mb-2 text-center whitespace-nowrap">
                  {category.name}
                </div>
                <div className="w-24 h-24 bg-white rounded-2xl overflow-hidden flex items-center justify-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <span className="text-4xl">{category.image}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Divider with "IN YOUR TOWN" */}
      <div className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs font-bold text-gray-500 tracking-wider">IN YOUR TOWN</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>
      </div>

      {/* Restaurant Sections - Horizontal Scrollable Cards */}
      <div className="pb-20">
        {loading ? (
          <div className="px-4 py-8">
            <CustomerLoader />
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No restaurants found</h3>
            <p className="text-gray-600 text-sm">Try adjusting your search</p>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-hide px-4">
            <div className="flex gap-4 pb-2">
              {/* Best Cafes Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex-shrink-0 w-80 bg-white rounded-3xl p-4"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
              >
                {/* Section Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Best Cafes</h2>
                    <p className="text-xs text-gray-600">The best offers at top restaurants</p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </div>

                {/* Restaurant List */}
                <div className="space-y-3">
                  {filteredRestaurants.slice(0, 4).map((restaurant, index) => (
                    <motion.div
                      key={restaurant.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleRestaurantClick(restaurant)}
                      className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-xl transition-colors"
                    >
                      {/* Restaurant Image */}
                      <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-orange-100 to-orange-200">
                        {restaurant.banner_url || restaurant.image_url || restaurant.logo_url ? (
                          <img
                            src={restaurant.banner_url || restaurant.image_url || restaurant.logo_url}
                            alt={restaurant.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-200"><span class="text-2xl">🍽️</span></div>'
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-2xl">🍽️</span>
                          </div>
                        )}
                      </div>

                      {/* Restaurant Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 truncate mb-0.5">
                          {restaurant.restaurant_name || restaurant.name || 'Restaurant'}
                        </h3>
                        <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                          <span>4.4 km</span>
                          <span>•</span>
                          <span className="capitalize truncate">Rohini</span>
                        </div>
                        {/* Offer Badge */}
                        <div className="flex items-center gap-1">
                          <TagIcon className="w-3 h-3 text-orange-500" />
                          <span className="text-xs font-bold text-orange-500">
                            Flat 25% OFF
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Buffet Card */}
              {filteredRestaurants.length > 4 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="flex-shrink-0 w-80 bg-white rounded-3xl p-4"
                  style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
                >
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Buffet</h2>
                      <p className="text-xs text-gray-600">All you can eat deals</p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  </div>

                  {/* Restaurant List */}
                  <div className="space-y-3">
                    {filteredRestaurants.slice(4, 8).map((restaurant, index) => (
                      <motion.div
                        key={restaurant.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => handleRestaurantClick(restaurant)}
                        className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-xl transition-colors"
                      >
                        {/* Restaurant Image */}
                        <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-green-100 to-green-200">
                          {restaurant.banner_url || restaurant.image_url || restaurant.logo_url ? (
                            <img
                              src={restaurant.banner_url || restaurant.image_url || restaurant.logo_url}
                              alt={restaurant.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200"><span class="text-2xl">🍽️</span></div>'
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-2xl">🍽️</span>
                            </div>
                          )}
                        </div>

                        {/* Restaurant Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate mb-0.5">
                            {restaurant.restaurant_name || restaurant.name || 'Restaurant'}
                          </h3>
                          <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                            <span>4.8 km</span>
                            <span>•</span>
                            <span className="capitalize truncate">Netaji Subhash Pl...</span>
                          </div>
                          {/* Offer Badge */}
                          <div className="flex items-center gap-1">
                            <TagIcon className="w-3 h-3 text-orange-500" />
                            <span className="text-xs font-bold text-orange-500">
                              Flat 25% OFF on buffet
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Premium Dining Card */}
              {filteredRestaurants.length > 8 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex-shrink-0 w-80 bg-white rounded-3xl p-4"
                  style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
                >
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Premium Dining</h2>
                      <p className="text-xs text-gray-600">Fine dining experience</p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  </div>

                  {/* Restaurant List */}
                  <div className="space-y-3">
                    {filteredRestaurants.slice(8, 12).map((restaurant, index) => (
                      <motion.div
                        key={restaurant.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => handleRestaurantClick(restaurant)}
                        className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-xl transition-colors"
                      >
                        {/* Restaurant Image */}
                        <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200">
                          {restaurant.banner_url || restaurant.image_url || restaurant.logo_url ? (
                            <img
                              src={restaurant.banner_url || restaurant.image_url || restaurant.logo_url}
                              alt={restaurant.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200"><span class="text-2xl">🍽️</span></div>'
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-2xl">🍽️</span>
                            </div>
                          )}
                        </div>

                        {/* Restaurant Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate mb-0.5">
                            {restaurant.restaurant_name || restaurant.name || 'Restaurant'}
                          </h3>
                          <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                            <span>5.2 km</span>
                            <span>•</span>
                            <span className="capitalize truncate">Ashok Vihar Phas...</span>
                          </div>
                          {/* Offer Badge */}
                          <div className="flex items-center gap-1">
                            <TagIcon className="w-3 h-3 text-orange-500" />
                            <span className="text-xs font-bold text-orange-500">
                              Flat 45% OFF
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Coffee Corner Card */}
              {filteredRestaurants.length > 12 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex-shrink-0 w-80 bg-white rounded-3xl p-4"
                  style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
                >
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Coffee Corner</h2>
                      <p className="text-xs text-gray-600">Cafes & desserts</p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  </div>

                  {/* Restaurant List */}
                  <div className="space-y-3">
                    {filteredRestaurants.slice(12, 16).map((restaurant, index) => (
                      <motion.div
                        key={restaurant.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => handleRestaurantClick(restaurant)}
                        className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-xl transition-colors"
                      >
                        {/* Restaurant Image */}
                        <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-yellow-100 to-yellow-200">
                          {restaurant.banner_url || restaurant.image_url || restaurant.logo_url ? (
                            <img
                              src={restaurant.banner_url || restaurant.image_url || restaurant.logo_url}
                              alt={restaurant.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-100 to-yellow-200"><span class="text-2xl">☕</span></div>'
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-2xl">☕</span>
                            </div>
                          )}
                        </div>

                        {/* Restaurant Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate mb-0.5">
                            {restaurant.restaurant_name || restaurant.name || 'Restaurant'}
                          </h3>
                          <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                            <span>3.2 km</span>
                            <span>•</span>
                            <span className="capitalize truncate">Connaught Place</span>
                          </div>
                          {/* Offer Badge */}
                          <div className="flex items-center gap-1">
                            <TagIcon className="w-3 h-3 text-orange-500" />
                            <span className="text-xs font-bold text-orange-500">
                              Flat 30% OFF
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}
      {/* Simple Footer for Partners */}
      <div className="px-4 py-8 bg-gray-50 border-t border-gray-100 mt-8 mb-20">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm font-medium text-gray-500 mb-4">
            Want to see your restaurant here?
          </p>
          <a 
            href="https://partner.ordyrr.com" 
            className="inline-flex items-center gap-2 px-6 py-2 bg-black text-white rounded-full text-sm font-bold shadow-lg hover:bg-gray-800 transition-colors"
          >
            <BuildingStorefrontIcon className="w-4 h-4 text-orange-400" />
            FOR RESTAURANTS
          </a>
        </div>
      </div>
      </div>
    </div>
  )
}

export default RestaurantDiscovery
